import { defineComponent, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import * as echarts from 'echarts'
import type { ECharts } from 'echarts'
import { useStatsStore } from '@/stores/stats-store'
import { useRegionStore } from '@/stores/region-store'
import {
  fetchRecentRecords,
  extractSpecialNum,
  extractRegularNums,
} from '@/composables/use-lotto-stats'

interface ChartData {
  xAxisData: string[]
  seriesData: number[]
  lineColor: string
}

const buildOption = (data: ChartData) => {
  const base: Record<string, unknown> = {
    backgroundColor: '#fff',
    grid: { top: 30, left: 35, bottom: 40, right: 5 },
    tooltip: { show: true },
    xAxis: {
      type: 'category',
      data: data.xAxisData,
      offset: 10,
      axisTick: { show: false },
      axisLine: { lineStyle: { color: '#ccc' } },
      axisLabel: { color: '#333' },
      splitArea: { interval: 1 },
    },
    graphic: [
      {
        type: 'text',
        left: 4,
        bottom: 10,
        style: {
          text: '号码:',
          fill: '#333',
          font: 'bold 12px sans-serif',
        },
      },
    ],
    yAxis: {
      type: 'value',
      name: '(次)',
      nameTextStyle: {
        fontSize: 16,
        verticalAlign: 'bottom',
        color: '#333',
        padding: [0, 0, -5, -30],
      },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    dataZoom: [{ type: 'inside', start: 0, end: 50, xAxisIndex: [0] }],
    series: [
      {
        data: data.seriesData,
        type: 'line',
        symbol: 'circle',
        symbolSize: 10,
        label: { show: true },
        lineStyle: { color: data.lineColor },
        itemStyle: { color: data.lineColor },
      },
    ],
  }
  if (data.xAxisData.length === 0) {
    base.title = {
      text: '暂无数据',
      top: 'middle',
      left: 'center',
      textStyle: { color: '#999', fontWeight: 'normal' },
    }
  }
  return base
}

export default defineComponent({
  name: 'NumberHistoryChart',
  props: {
    type: { type: Number, required: true },
  },
  setup(props) {
    const statsStore = useStatsStore()
    const regionStore = useRegionStore()
    const chart1Ref = ref<HTMLDivElement | null>(null)
    const chart2Ref = ref<HTMLDivElement | null>(null)
    let inst1: ECharts | null = null
    let inst2: ECharts | null = null
    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    const refresh = async () => {
      const periodCount = statsStore.periodCount
      if (periodCount == null) return
      const records = await fetchRecentRecords(regionStore.region, periodCount)
      const extractor = props.type === 2 ? extractSpecialNum : extractRegularNums

      const freq = new Map<number, number>()
      for (let n = 1; n <= 49; n++) freq.set(n, 0)
      for (const r of records) {
        for (const num of extractor(r)) {
          freq.set(num, (freq.get(num) ?? 0) + 1)
        }
      }

      // 热图:只包含出现过的号码,按出现次数降序,同次数按号码升序
      const hotEntries = [...freq.entries()]
        .filter(([, c]) => c > 0)
        .sort((a, b) => b[1] - a[1] || a[0] - b[0])

      // 冷图:全部 1-49,冷值 = periodCount - 出现次数,按冷值降序,同冷值按号码升序
      const coldEntries = [...freq.entries()]
        .map(([n, c]) => [n, periodCount - c] as [number, number])
        .sort((a, b) => b[1] - a[1] || a[0] - b[0])

      const hot: ChartData = {
        xAxisData: hotEntries.map(([n]) => String(n).padStart(2, '0')),
        seriesData: hotEntries.map(([, c]) => c),
        lineColor: '#ea4c2d',
      }
      const cold: ChartData = {
        xAxisData: coldEntries.map(([n]) => String(n).padStart(2, '0')),
        seriesData: coldEntries.map(([, c]) => c),
        lineColor: '#3C96C3',
      }

      inst1?.setOption(buildOption(hot), true)
      inst2?.setOption(buildOption(cold), true)
    }

    const debouncedRefresh = () => {
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(refresh, 300)
    }

    onMounted(() => {
      if (chart1Ref.value) inst1 = echarts.init(chart1Ref.value)
      if (chart2Ref.value) inst2 = echarts.init(chart2Ref.value)
      refresh()
    })

    onBeforeUnmount(() => {
      inst1?.dispose()
      inst2?.dispose()
      inst1 = null
      inst2 = null
    })

    watch(() => [statsStore.periodCount, regionStore.region, props.type], debouncedRefresh)

    return () => (
      <div class="mint-tab-container-wrap">
        <div class="mint-tab-container-item">
          <div class="title-box">
            <h4>当前统计期数:{statsStore.periodCount}</h4>
            <p>{props.type === 2 ? '特码' : '正码'}历史热图(所选期数范围国内出现的次数)</p>
          </div>
          <div ref={chart1Ref} class="chart" />
          <div class="title-box">
            <p>{props.type === 2 ? '特码' : '正码'}历史冷图(所选期数范围国内出现的次数)</p>
          </div>
          <div ref={chart2Ref} class="chart" />
        </div>
      </div>
    )
  },
})
